// The door in front of the internal screens.
//
// **One check, two screens.** `/tuner` and `/features` are the same audience —
// whoever is running the deployment — and a second copy of a password flow is
// a second place for the wrong-password path to go wrong. That is the argument
// `GatePage` itself already carries about the site gate and this one; this is
// the same argument a level up, about the *check* rather than the form.
//
// **The gate is only ever the visible half**, and the two screens differ in
// what is behind it. `/tuner` has a server that refuses `/api/tuner` without
// the same cookie, so skipping the page buys nothing. `/features` shows the
// rollout table and `/api/flags`, and **neither is secret from the app** — the
// table ships in the bundle and the endpoint has to answer before anybody has
// typed a password, or the game could not decide what to draw. So the gate
// there keeps the *screen* internal rather than the data, and that is worth
// being plain about rather than implying a protection it does not have.

import { useEffect, useState } from "react";

import { TUNER_API_PATH } from "src/engine/net/tuner";
import { GatePage } from "./GatePage";

/**
 * Whether this browser has answered the dashboard password.
 *
 * `undefined` while the question is still out, and callers render nothing in
 * that moment on purpose: flashing a gate in front of somebody who is already
 * through it looks like the site changed its mind about letting them in.
 */
export function useTunerGate(): { open: boolean | undefined; unlock: () => void } {
  const [open, setOpen] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    let live = true;
    void fetch(`${TUNER_API_PATH}/session`, { credentials: "same-origin" })
      .then((response) => (response.ok ? response.json() : { unlocked: false }))
      .then((body: { unlocked?: boolean }) => live && setOpen(Boolean(body.unlocked)))
      // Unreachable server reads as locked: showing an internal screen because
      // the question could not be asked is the one answer that must not happen.
      .catch(() => live && setOpen(false))
      .finally(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  return { open, unlock: () => setOpen(true) };
}

/**
 * Render `children` once the dashboard password has been answered.
 *
 * `title` and `blurb` are the screen's own — the two pages are behind one door
 * and are not the same room, so the form says which one you are knocking on.
 */
export function TunerGated({
  title,
  blurb,
  children
}: {
  title: string;
  blurb: string;
  children: React.ReactNode;
}): React.ReactElement | null {
  const { open, unlock } = useTunerGate();
  if (open === undefined) return null;
  if (!open) {
    return (
      <GatePage
        endpoint={`${TUNER_API_PATH}/unlock`}
        title={title}
        blurb={blurb}
        notice={
          <>
            <strong>This view is private.</strong> It shows work in progress on
            the game rather than anything about your own games. Enter the
            password to continue.
          </>
        }
        onUnlock={unlock}
      />
    );
  }
  return <>{children}</>;
}
